import { QueryError } from '@graphql-box/helpers';
import { Clear } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { PosterImage } from '#components/PosterImage/index.tsx';
import { TileCarousel } from '#components/TileCarousel/index.tsx';
import { formatMoviesAndTvResults } from '#helpers/formatMoviesAndTvResults.ts';
import { useMovieTvMultiQuery } from '#hooks/useMovieTvMultiQuery.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';

export type ContinueWatchingCarouselProps = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  setActivePreview?: (item: ActivePreview) => void;
};

export const ContinueWatchingCarousel = ({ children, setActivePreview }: ContinueWatchingCarouselProps) => {
  const { config } = useToolbelt();
  const continueWatchingTv = useStore(store => store.continueWatchingTv);
  const removeContinueWatchingTv = useStore(store => store.removeContinueWatchingTv);

  const {
    execute,
    tvLoading,
    tvResults = [],
  } = useMovieTvMultiQuery({
    initiator: 'ContinueWatchingCarousel',
  });

  const depsKey = JSON.stringify(continueWatchingTv);

  useEffect(() => {
    const tvIds = continueWatchingTv.map(([id]) => id);
    // @ts-expect-error tv ids are always strings
    execute(undefined, tvIds);
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  if (tvResults.length > 0 && tvResults.every(result => result instanceof QueryError)) {
    console.error(tvResults);
    return;
  }

  const results: Tv[] = [];
  const errors: QueryError[] = [];

  for (const result of tvResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      results.push(result.data.tv);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  const items = formatMoviesAndTvResults(results, ScreenType.Tv, config);

  if (!tvLoading && items.length === 0) {
    return;
  }

  const handleRemove = (id: string) => () => {
    removeContinueWatchingTv(id);
  };

  const tileCarousel = (
    <TileCarousel
      items={items}
      renderImage={props => (
        <PosterImage
          {...props}
          renderAdditionalOptionItems={() => (
            // @ts-expect-error id is always a string
            // eslint-disable-next-line react/prop-types
            <MenuItem onClick={handleRemove(props.id)}>
              <ListItemIcon>
                <Clear fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remove continue watching</ListItemText>
            </MenuItem>
          )}
        />
      )}
      setActivePreview={setActivePreview}
    />
  );

  return children ? children(tileCarousel) : tileCarousel;
};
